import { ChangeDetectionStrategy, Component, computed, input, output, OutputEmitterRef } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AvatarDirective } from '../../../common/avatar/avatar.directive';
import { EllipsisTooltipDirective } from '../../../common/ellipsis-tooltip/ellipsis-tooltip.directive';
import { IconComponent } from '../../../common/icon/icon.component';
import { UserNamePipe } from '../../../common/user-name/user-name.pipe';
import { SlugPipe } from '../../pipes/slug.pipe';
import { MatIconButton } from '@angular/material/button';
import { NgClass } from '@angular/common';
import { I18NextModule } from 'angular-i18next';
import { getDefaultModalSettings } from '../../../common/utils/helpers';
import { NotLoggedService } from '../../../common/not-logged/not-logged.service';
import { OpenDialogService } from '../../../common/open-dialog/open-dialog.service';
import { map } from 'rxjs';
import { GetSharryContentCardBookmarkedGQL } from '../content-card/content-card.query.generated';

@Component({
  selector: 'sharry-post-card',
  standalone: true,
  templateUrl: './post-card.component.html',
  styleUrls: ['./post-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AvatarDirective,
    EllipsisTooltipDirective,
    UserNamePipe,
    IconComponent,
    SlugPipe,
    RouterLink,
    MatIconButton,
    NgClass,
    I18NextModule,
  ],
})
export class PostCardComponent {
  post = input<any>();
  bookmarkedChanged: OutputEmitterRef<any> = output<any>();

  constructor(
    private notLoggedService: NotLoggedService,
    private openDialogService: OpenDialogService,
    private getSharryContentCardBookmarkedGQL: GetSharryContentCardBookmarkedGQL,
  ) {}

  owner = computed(() => this.post()?.owner);
  bookmarked = computed(() => this.post()?.bookmarked);
  shortAddress = computed(() => this.post()?.shortAddress);
  title = computed(() => this.post()?.title);
  coverPictureUrl = computed(() => this.post().coverPicture?.bestVersion?.url);
  hasContentLikes = computed(() => this.post()?.contentLikes?.length > 0);
  contentLikes = computed(() => this.post()?.contentLikes?.length);
  createdAt = computed(() =>
    this.timeStampToShortDate(this.post()?.createdAt ?? new Date(), {
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
  );

  async onLike() {
    const { ManageLikesComponent } = await import('../../dialogs/manage-likes/manage-likes.component');

    const handleLikeDialog = () => {
      this.openDialogService
        .openDialog<any, { content?: any; currentLikeFolderId?: string }>(ManageLikesComponent, {
          ...getDefaultModalSettings(),
          data: {
            content: { ...this.post() },
          },
        })
        .afterClosed()
        .subscribe(() => {
          this.getSharryContentCardBookmarkedGQL
            .fetch({ id: this.post().id }, { fetchPolicy: 'no-cache' })
            .pipe(map(r => r.data.sharryContent.bookmarked))
            .subscribe((bookmarked: any) => {
              this.bookmarkedChanged.emit({ ...this.post(), bookmarked });
            });
        });
    };
    this.notLoggedService.execute(() => handleLikeDialog());
  }

  timeStampToShortDate(
    date: string,
    options?: {
      day?: 'numeric' | '2-digit';
      month?: 'long' | 'short';
      year?: 'numeric' | '2-digit';
      hour?: 'numeric' | '2-digit';
      minute?: 'numeric' | '2-digit';
      second?: 'numeric' | '2-digit';
    },
  ) {
    return new Intl.DateTimeFormat('it-IT', {
      day: options?.day || 'numeric',
      month: options?.month || 'short',
      year: options?.year,
      hour: options?.hour,
      minute: options?.minute,
      second: options?.second,
    }).format(new Date(date));
  }
}
